/**
 * DADDL motion toggle — pause / play for self-moving content (WCAG 2.2.2).
 * Markup: snippets/daddl-motion-toggle.liquid. Loaded from daddl-theme-head.
 *
 * The button is a toggle: aria-pressed="true" means the motion is paused.
 * The target element gets `is-motion-paused` and a bubbling
 * `daddl-motion:change` event ({ detail: { paused } }). Plain looping videos
 * inside the target opt in with `data-daddl-motion-video` and are handled here;
 * sections with their own playback logic listen for the event instead.
 *
 * `data-remember="key"` keeps a shopper's pause across page loads
 * (localStorage `daddl-motion:key`). Only clicks are stored; the
 * reduced-motion start state is not.
 *
 * Decorative autoplay loops without a toggle (video hero, claims media) carry
 * `data-daddl-motion-video` too; they are stopped here under
 * prefers-reduced-motion, including sections re-rendered in the theme editor.
 */
class DaddlMotionToggle extends HTMLElement {
  connectedCallback() {
    if (this.button) return;
    this.button = this.querySelector('[data-daddl-motion-button]');
    this.target = document.getElementById(this.dataset.target) || this.parentElement;
    if (!this.button || !this.target) return;

    this.paused = false;
    this.storageKey = this.dataset.remember ? `daddl-motion:${this.dataset.remember}` : null;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if ((reduce && this.hasAttribute('data-pause-on-reduced-motion')) || this.readRemembered()) this.set(true);

    this.button.addEventListener('click', () => {
      this.set(!this.paused);
      this.remember();
    });
  }

  /* Remembered pause (storage can throw in private mode; treat as unset). */
  readRemembered() {
    if (!this.storageKey) return false;
    try {
      return window.localStorage.getItem(this.storageKey) === 'paused';
    } catch (error) {
      return false;
    }
  }

  remember() {
    if (!this.storageKey) return;
    try {
      if (this.paused) {
        window.localStorage.setItem(this.storageKey, 'paused');
      } else {
        window.localStorage.removeItem(this.storageKey);
      }
    } catch (error) {
      /* Storage unavailable: the pause still applies to this page. */
    }
  }

  /* Single state change: button state, target class, opt-in videos, event. */
  set(paused) {
    this.paused = paused;
    this.button.setAttribute('aria-pressed', String(paused));
    this.target.classList.toggle('is-motion-paused', paused);
    this.target.querySelectorAll('video[data-daddl-motion-video]').forEach((video) => {
      if (paused) {
        video.pause();
      } else {
        const playing = video.play();
        if (playing && playing.catch) playing.catch(() => {});
      }
    });
    this.target.dispatchEvent(new CustomEvent('daddl-motion:change', { bubbles: true, detail: { paused } }));
  }
}

if (!customElements.get('daddl-motion-toggle')) {
  customElements.define('daddl-motion-toggle', DaddlMotionToggle);
}

/* Reduced motion: decorative loops stay on their poster / first frame. */
(function stillDecorativeLoops() {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const still = (scope) => {
    if (!reduce.matches) return;
    scope.querySelectorAll('video[data-daddl-motion-video]').forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause();
    });
  };

  still(document);
  document.addEventListener('shopify:section:load', (event) => still(event.target));
})();
